import { useCallback, type ReactNode } from "react";
import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import { roundDone } from "./useRoundLock";
import { roundTypeAt, type Draft } from "@heraion/shared";

import { Icon } from "../Icon/Icon";
import type { TFn } from "../i18nTypes";
import { RoundTypeBadge, useRoundTypeWords } from "../primitives/RoundTypeBadge";
import type { UiTheme } from "../theme";

/** What the heatmap draws under a week head, and says for it. */
export type WeekMark = { badge: ReactNode; label: string } | null;

/** A round done: «done» in its spoken name, a bare ✓ on screen. */
export const doneWord = (t: TFn) => t("editorV3.lock.done", { defaultValue: "done" });
export function DoneCheck({ theme }: { theme: UiTheme }) {
  return <Icon name="check" size={theme.typography.sizes.sm} color={theme.palette.ink2} />;
}

// The heatmap's mark under a W (#336 S2, #358): a deload or test round's
// glyph, and a bare «✓» beside it on a round already done, narrow enough for
// a 44pt column. Glyphs, never colour alone; the words are the head's spoken
// name. The editor's heatmap and the program preview read it.
export function useWeekMark(theme: UiTheme, draft: Pick<Draft, "roundTypes">, lockedThrough = -1) {
  const { t } = useTranslation();
  const words = useRoundTypeWords();
  return useCallback(
    (w: number): WeekMark => {
      const type = roundTypeAt(draft, w);
      const done = roundDone(w, lockedThrough);
      if (type === "normal" && !done) return null;
      return {
        badge: (
          <View style={[local.marks, { gap: theme.spacing.s1 }]}>
            {done ? <DoneCheck theme={theme} /> : null}
            <RoundTypeBadge theme={theme} type={type} compact />
          </View>
        ),
        label: [
          type === "normal"
            ? t("editorV3.roundChipA11y", { n: w + 1, defaultValue: "Round {{n}}" })
            : t("editorV3.roundChipTypedA11y", { n: w + 1, type: words[type], defaultValue: "Round {{n}}, {{type}}" }),
          done ? doneWord(t) : null,
        ]
          .filter(Boolean)
          .join(", "),
      };
    },
    [draft, lockedThrough, t, theme, words],
  );
}

const local = StyleSheet.create({
  marks: { flexDirection: "row", alignItems: "center" },
});
